/**
 * Extracts the instance ID from the subdomain
 * Expected formats:
 * - {instanceId}.game.{origin}
 * - {instanceId}.{origin} (creator mode)
 */
export function getInstanceIdFromSubdomain(): string | null {
  if (typeof window === "undefined") {
    return null;
  }

  const hostname = window.location.hostname;

  // Handle localhost development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    // For development, we can use a query parameter as fallback
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("instanceId");
  }

  const origin = import.meta.env.VITE_ORIGIN;
  if (!origin) {
    console.warn("VITE_ORIGIN environment variable is not set");
    return null;
  }

  // Escape dots in origin for regex
  const escapedOrigin = origin.replace(/\./g, "\\.");

  // Extract subdomain from *.game.{origin} pattern
  const gameSubdomainMatch = hostname.match(
    new RegExp(`^([^.]+)\\.game\\.${escapedOrigin}$`),
  );
  if (gameSubdomainMatch) {
    return gameSubdomainMatch[1];
  }

  // Extract subdomain from *.{origin} pattern (creator mode)
  const directSubdomainMatch = hostname.match(
    new RegExp(`^([^.]+)\\.${escapedOrigin}$`),
  );
  if (directSubdomainMatch) {
    return directSubdomainMatch[1];
  }

  return null;
}

/**
 * Checks if the current hostname matches the expected game or creator subdomain pattern
 */
export function isGameSubdomain(): boolean {
  if (typeof window === "undefined") {
    return false;
  }

  const hostname = window.location.hostname;

  // Handle localhost development
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return true;
  }

  const origin = import.meta.env.VITE_ORIGIN;
  if (!origin) {
    console.warn("VITE_ORIGIN environment variable is not set");
    return false;
  }

  // Escape dots in origin for regex
  const escapedOrigin = origin.replace(/\./g, "\\.");

  // Check for game and creator subdomain patterns
  return (
    new RegExp(`^[^.]+\\.game\\.${escapedOrigin}$`).test(hostname) ||
    new RegExp(`^[^.]+\\.${escapedOrigin}$`).test(hostname)
  );
}
